import { Box, Button, Checkbox } from "@mui/material";
import React from "react";

import { useCompareTray } from "./compareTray";

export interface CompareToggleProps {
  id: string;
  // Null for a product no source names
  name: string | null;
  // A checkbox for a row or a card; a text button for the product page
  variant?: "checkbox" | "button";
}

// Why a seventh product cannot be picked
const FULL_TITLE = "Six products are picked: remove one to add another";

// Keeps a click or an Enter or Space press on the control from reaching the
// row or the card behind it, which would open the product
const stop = (event: React.SyntheticEvent) => event.stopPropagation();
const stopActivation = (event: React.KeyboardEvent) => {
  if (event.key === "Enter" || event.key === " ") {
    event.stopPropagation();
  }
};

/**
 * Picks one product for the comparison, or lets it go: it reads and writes the
 * compare tray. With six products picked, a further one cannot be added.
 */
const CompareToggle: React.FC<CompareToggleProps> = ({
  id,
  name,
  variant = "checkbox",
}) => {
  const tray = useCompareTray();
  const picked = tray.has(id);
  const blocked = tray.full && !picked;
  const toggle = () => tray.toggle({ id, name });

  if (variant === "button") {
    return (
      <Box component="span" title={blocked ? FULL_TITLE : undefined}>
        <Button
          size="small"
          variant="outlined"
          disabled={blocked}
          onClick={(event) => {
            stop(event);
            toggle();
          }}
          onKeyDown={stopActivation}
        >
          {picked ? "In comparison ✓" : "Add to comparison"}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="span"
      title={blocked ? FULL_TITLE : undefined}
      onClick={stop}
      onKeyDown={stopActivation}
      sx={{ display: "inline-flex", alignItems: "center" }}
    >
      <Checkbox
        size="small"
        checked={picked}
        disabled={blocked}
        onChange={toggle}
        inputProps={{
          "aria-label": name ? `Compare ${name}` : "Compare this product",
        }}
        sx={{ p: "2px" }}
      />
    </Box>
  );
};

export default CompareToggle;
